import * as React from 'react';
import moment from 'moment';
import { parse } from 'query-string';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from 'Src/models/tournament';
import { getTournamentCapacitySummary } from 'Src/helpers/tournament/tournament';
import { formatTournamentAges } from 'Src/helpers/ageGroups';
import { MODE_TYPE } from 'Src/consts/common';
import {
  TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING,
  TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING
} from 'Src/consts/tournament';
import { getTournament, getTournamentManagementSummary } from 'Src/helpers/service/adminSU/tournamentsTable';
import { isMiniTeamTournament, isTeamTournament } from 'Src/helpers/tournament/tournament';
import { Loader } from 'Src/components/Loader/Loader';
import { TournamentOverview } from './TournamentOverview';
import { EntriesSection, TournamentEntries } from './TournamentEntries';
import { CompetitionSection, TournamentCompetition, TournamentEventListState } from './TournamentCompetition';
import { TournamentReportsLauncher } from '../Reports/TournamentReportsLauncher';
import { TournamentPublishing } from './TournamentPublishing';
import { TournamentCommunications } from './TournamentCommunications';
import { SettingsSection, TournamentSettings } from './TournamentSettings';
import { UnionTournamentManagementPageHelp, UnionTournamentManagementTab } from './UnionTournamentManagementPageHelp';
import './TournamentManagement.scss';
import { UnsavedChangesModal } from 'Src/components/UnsavedChangesModal/UnsavedChangesModal';
import { getTournamentCommunicationsInbox } from 'Src/helpers/service/adminSU/tournamentCommunications';

type ManagementTab = 'overview' | 'entries' | 'competition' | 'communications' | 'publishing' | 'settings';
type CommunicationsSection = 'requests' | 'chats';

interface Props {
  user: AppUser;
  history: any;
  location: any;
  match: { params: { section?: ManagementTab; subsection?: EntriesSection | CompetitionSection | SettingsSection } };
}

interface State {
  tournament?: Tournament;
  isLoading: boolean;
  error: string;
  isReportsOpen: boolean;
  isSettingsDirty: boolean;
  isSettingsDiscardOpen: boolean;
  confirmationSummary: {
    total: number;
    pending: number;
    rejected: number;
  };
  isConfirmationSummaryLoading: boolean;
  communicationsUnreadCount: number;
}

type BookingTone = 'open' | 'info' | 'warning' | 'danger' | 'neutral' | 'manual';

interface BookingStatus {
  label: string;
  tone: BookingTone;
  description: string;
}

const TABS: Array<{ key: ManagementTab; label: string; icon: string }> = [
  { key: 'overview', label: 'Overview', icon: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z' },
  {
    key: 'entries',
    label: 'Entries',
    icon:
      'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75'
  },
  { key: 'competition', label: 'Competition', icon: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z' },
  { key: 'communications', label: 'Communications', icon: 'M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z' },
  {
    key: 'publishing',
    label: 'Publishing',
    icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20'
  },
  {
    key: 'settings',
    label: 'Settings',
    icon:
      'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-2.87 1.22V21a2 2 0 1 1-4 0 1.7 1.7 0 0 0-2.87-1.22l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 3 14a2 2 0 1 1 0-4 1.7 1.7 0 0 0 1.22-2.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 10 3a2 2 0 1 1 4 0 1.7 1.7 0 0 0 2.87 1.22l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 21 10a2 2 0 1 1 0 4 1.7 1.7 0 0 0-1.6 1z'
  }
];

const ENTRIES_BREADCRUMB_LABELS: { [section in EntriesSection]: string } = {
  rules: 'Rules & capacity',
  eligible: 'Eligible schools',
  participants: 'Participants',
  athleteSchools: 'Athletes’ schools',
  schoolAdmins: 'School admins',
  waiting: 'Waiting list',
  requests: 'Confirmation requests',
  log: 'Enrolment log'
};

const COMPETITION_BREADCRUMB_LABELS: { [section in CompetitionSection]: string } = {
  teams: 'Teams',
  groups: 'Groups',
  pitches: 'Pitches',
  fixtures: 'Fixtures',
  competitors: 'Competitors',
  events: 'Events'
};

const getSportColours = (sportName: string): { color: string; backgroundColor: string } => {
  const colours: { [sport: string]: string } = {
    Football: '#227ebd',
    Netball: '#d64550',
    Hockey: '#2f9e5a',
    Athletics: '#f08c1a',
    'After School Club': '#7b5bd6'
  };
  const color = colours[sportName];

  if (color) {
    return { color, backgroundColor: `${color}1a` };
  }

  const hash = String(sportName || 'sport')
    .split('')
    .reduce((value, char) => ((value << 5) - value + char.charCodeAt(0)) | 0, 0);
  const hue = Math.abs(hash) % 360;
  return {
    color: `hsl(${hue}, 58%, 42%)`,
    backgroundColor: `hsla(${hue}, 58%, 47%, 0.1)`
  };
};

const META_ICONS = {
  calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 7v5l3 2',
  pin: 'M12 22s7-6.13 7-12a7 7 0 1 0-14 0c0 5.87 7 12 7 12zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  audience:
    'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  notifications: 'M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4',
  edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z'
};

const MetaIcon = ({ path }: { path: string }) => (
  <svg className="utmManagement__metaIcon" viewBox="0 0 24 24" aria-hidden="true">
    <path d={path} />
  </svg>
);

const getBookingStatus = (tournament: Tournament): BookingStatus => {
  const hasOpeningDate = Boolean(tournament.entriesOpen) && moment(tournament.entriesOpen).isValid();
  const hasClosingDate = Boolean(tournament.deadlineForEntries) && moment(tournament.deadlineForEntries).isValid();

  if (!hasOpeningDate && !hasClosingDate) {
    return {
      label: 'No booking window',
      tone: 'neutral',
      description: 'Booking opening and closing dates have not been configured.'
    };
  }

  if (!hasOpeningDate || !hasClosingDate) {
    return {
      label: 'Incomplete booking window',
      tone: 'danger',
      description: 'Both a booking opening date and a booking closing date are required.'
    };
  }

  const now = moment();
  const opensAt = moment(tournament.entriesOpen);
  const closesAt = moment(tournament.deadlineForEntries);

  if (opensAt.isAfter(closesAt)) {
    return {
      label: 'Invalid booking window',
      tone: 'danger',
      description: 'The booking opening date is later than the booking closing date.'
    };
  }

  if (now.isBefore(opensAt)) {
    return {
      label: `Booking opens ${opensAt.format('DD/MM/YY')}`,
      tone: 'info',
      description: `Booking opens on ${opensAt.format('DD MMM YYYY [at] HH:mm')}.`
    };
  }

  if (now.isAfter(closesAt)) {
    return {
      label: 'Closed',
      tone: 'neutral',
      description: `The booking window closed on ${closesAt.format('DD MMM YYYY [at] HH:mm')}.`
    };
  }

  if (tournament.isReadOnly) {
    return {
      label: 'Booking unavailable, Schools read-only',
      tone: 'warning',
      description: 'The booking window is open, but schools are currently in read-only mode.'
    };
  }

  if (!tournament.isAutoEnrollmentTeams) {
    return {
      label: 'Manual enrolment only',
      tone: 'manual',
      description: 'The booking window is open, but auto-enrolment is switched off.'
    };
  }

  if (tournament.isAutoEnrollmentToWaitingList) {
    return {
      label: 'Open, Booking onto WL',
      tone: 'warning',
      description: 'All new enrolments are being added directly to the waiting list.'
    };
  }

  const settings = tournament.autoEnrollmentTeamsSettings || [];

  if (settings.length === 0) {
    return {
      label: 'Open, Capacity not set',
      tone: 'danger',
      description: 'Auto-enrolment is on, but no capacity rules have been configured.'
    };
  }

  const capacitySummary = getTournamentCapacitySummary(tournament);
  const hasUnlimitedPlaces = capacitySummary.unlimited;
  const hasFreePlaces = capacitySummary.booked < capacitySummary.capacity;

  if (hasUnlimitedPlaces || hasFreePlaces) {
    return {
      label: 'Open',
      tone: 'open',
      description: 'The booking window is open and places are available.'
    };
  }

  if (tournament.isEnableWaitingListWhenFull) {
    return {
      label: 'Full, Booking onto WL',
      tone: 'warning',
      description: 'All places are taken; new enrolments are being added to the waiting list.'
    };
  }

  return {
    label: 'Full',
    tone: 'danger',
    description: 'All places are taken and waiting-list booking is not enabled.'
  };
};

export class TournamentManagement extends React.Component<Props, State> {
  state: State = {
    isLoading: true,
    error: '',
    isReportsOpen: false,
    isSettingsDirty: false,
    isSettingsDiscardOpen: false,
    confirmationSummary: { total: 0, pending: 0, rejected: 0 },
    isConfirmationSummaryLoading: true,
    communicationsUnreadCount: 0
  };

  pendingSettingsNavigation?: () => void;
  communicationsUnreadTimer?: number;

  requestSettingsNavigation = (navigate: () => void) => {
    if (this.getActiveTab() === 'settings' && this.state.isSettingsDirty) {
      this.pendingSettingsNavigation = navigate;
      this.setState({ isSettingsDiscardOpen: true });
      return;
    }
    navigate();
  };

  componentDidMount() {
    const tournamentId = this.getTournamentId();

    if (!tournamentId) {
      this.setState({ isLoading: false, error: 'Tournament was not specified.' });
      return;
    }

    getTournament(this.props.user, tournamentId)
      .then(tournament => {
        this.setState({ tournament, isLoading: false });
        this.updateTopbarBreadcrumb(tournament);
        this.loadConfirmationSummary(tournament.id);
        this.loadCommunicationsUnreadCount(tournament.id);
      })
      .catch(() => this.setState({ isLoading: false, error: 'We could not load this tournament.' }));
    this.communicationsUnreadTimer = window.setInterval(() => this.loadCommunicationsUnreadCount(tournamentId), 30000);
  }

  loadCommunicationsUnreadCount = (tournamentId: string) => {
    getTournamentCommunicationsInbox(this.props.user, { unread: true, limit: 100 })
      .then(items =>
        this.setState({
          communicationsUnreadCount: items.filter(
            item => String(item.tournamentId || (item.tournament && (item.tournament.id || item.tournament._id))) === String(tournamentId) && item.unread !== false
          ).length
        })
      )
      .catch(() => undefined);
  };

  componentDidUpdate(prevProps: Props) {
    const previousParams = prevProps.match.params;
    const currentParams = this.props.match.params;

    if (
      this.state.tournament &&
      (previousParams.section !== currentParams.section || previousParams.subsection !== currentParams.subsection)
    ) {
      this.updateTopbarBreadcrumb(this.state.tournament);
    }
  }

  componentWillUnmount() {
    if (this.communicationsUnreadTimer) {
      window.clearInterval(this.communicationsUnreadTimer);
    }
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  getTournamentId = (): string => {
    const query = parse(this.props.location.search);
    const value = query.tournament;
    return Array.isArray(value) ? value[0] : value || '';
  };

  getActiveTab = (): ManagementTab => {
    const section = this.props.match.params.section;
    if (section === 'entries' && this.props.match.params.subsection === 'requests') return 'communications';
    if (section && TABS.some(tab => tab.key === section)) {
      return section;
    }
    return 'overview';
  };

  loadConfirmationSummary = (tournamentId: string) => {
    getTournamentManagementSummary(this.props.user, tournamentId)
      .then(summary =>
        this.setState({
          confirmationSummary: {
            total: summary.confirmationTotal,
            pending: summary.confirmationPending,
            rejected: summary.confirmationRejected
          },
          isConfirmationSummaryLoading: false
        })
      )
      .catch(() => this.setState({ isConfirmationSummaryLoading: false }));
  };

  updateTopbarBreadcrumb = (tournament: Tournament) => {
    const activeTab = this.getActiveTab();
    const parts = ['Tournaments', tournament.name];

    if (activeTab === 'entries') {
      const requestedSection = this.props.match.params.subsection as EntriesSection;
      const section = ENTRIES_BREADCRUMB_LABELS[requestedSection] ? requestedSection : 'rules';
      parts.push('Entries', ENTRIES_BREADCRUMB_LABELS[section]);
    } else if (activeTab === 'competition') {
      const isTeam = isTeamTournament(tournament) || isMiniTeamTournament(tournament);
      const requestedSection = this.props.match.params.subsection as CompetitionSection;
      const validTeamSection = ['teams', 'groups', 'pitches', 'fixtures'].indexOf(requestedSection) !== -1;
      const validIndividualSection = ['competitors', 'teams', 'events'].indexOf(requestedSection) !== -1;
      const section: CompetitionSection = isTeam
        ? validTeamSection
          ? requestedSection
          : 'teams'
        : validIndividualSection
        ? requestedSection
        : 'competitors';
      parts.push('Competition', COMPETITION_BREADCRUMB_LABELS[section]);
    } else if (activeTab === 'publishing') {
      parts.push('Publishing');
    } else if (activeTab === 'communications') {
      parts.push('Communications', this.getCommunicationsSection() === 'requests' ? 'Confirmation requests' : 'Chats');
    } else if (activeTab === 'settings') {
      const settingsSection =
        this.props.match.params.subsection === 'admins'
          ? 'Admins'
          : this.props.match.params.subsection === 'communications'
          ? 'Communications'
          : 'Details';
      parts.push('Settings', settingsSection);
    }

    window.dispatchEvent(
      new CustomEvent('sit:topbar-breadcrumb', {
        detail: parts.join(' / ')
      })
    );
  };

  onBackClick = () => {
    this.requestSettingsNavigation(this.goBack);
  };

  goBack = () => {
    const previousState = this.props.location.state || {};
    const tournamentListState = previousState.tournamentListState || {};
    this.props.history.push({
      pathname: previousState.prevPath || '/tournaments/upcoming',
      search: previousState.search || '',
      state: tournamentListState
    });
  };

  onTabClick = (tab: ManagementTab) => {
    if (tab === this.getActiveTab()) return;
    this.requestSettingsNavigation(() => this.navigateToTab(tab));
  };

  navigateToTab = (tab: ManagementTab) => {
    this.props.history.push({
      pathname: `/tournaments/manage/${tab}`,
      search: this.props.location.search,
      state: this.props.location.state
    });
  };

  onEditTournamentClick = () => {
    this.requestSettingsNavigation(this.navigateToTournamentEdit);
  };

  navigateToTournamentEdit = () => {
    this.props.history.push({
      pathname: '/tournaments/tournamentView',
      search: `tournament=${this.getTournamentId()}&mode=${MODE_TYPE.EDIT}`,
      state: this.props.location.state
    });
  };

  onShowNotificationsClick = () => {
    this.requestSettingsNavigation(() => {
      this.props.history.push({
        pathname: '/tournaments/notifications',
        search: `tournament=${this.getTournamentId()}`,
        state: {
          prevPath: this.props.location.pathname,
          search: this.props.location.search,
          tournament: this.state.tournament
        }
      });
    });
  };

  onOpenTournamentSection = (path: string) => {
    if (path === 'participantsLegacy') {
      this.props.history.push({
        pathname: '/tournaments/participants',
        search: `tournament=${this.getTournamentId()}`,
        state: this.props.location.state
      });
      return;
    }
    const entriesSections: { [path: string]: EntriesSection } = {
      eligibleSchools: 'eligible',
      participants: 'participants',
      waitingList: 'waiting',
      confirmationRequests: 'requests',
      logs: 'log'
    };
    if (entriesSections[path]) {
      this.onEntriesSectionChange(entriesSections[path]);
      return;
    }
    if (path === 'publishing') {
      this.onTabClick('publishing');
      return;
    }
    this.props.history.push({
      pathname: `/tournaments/${path}`,
      search: `tournament=${this.getTournamentId()}`,
      state: this.props.location.state
    });
  };

  onEntriesSectionChange = (section: EntriesSection) => {
    if (section === 'requests') {
      this.onCommunicationsSectionChange('requests');
      return;
    }
    this.props.history.push({
      pathname: `/tournaments/manage/entries/${section}`,
      search: this.props.location.search,
      state: this.props.location.state
    });
  };

  getCommunicationsSection = (): CommunicationsSection =>
    this.props.match.params.subsection === 'requests' ? 'requests' : 'chats';

  onCommunicationsSectionChange = (section: CommunicationsSection) => {
    this.props.history.push({
      pathname: `/tournaments/manage/communications/${section}`,
      search: this.props.location.search,
      state: this.props.location.state
    });
  };

  onCompetitionSectionChange = (section: CompetitionSection) => {
    this.props.history.push({
      pathname: `/tournaments/manage/competition/${section}`,
      search: this.props.location.search,
      state: this.props.location.state
    });
  };

  onSettingsSectionChange = (section: SettingsSection) => {
    this.props.history.push({
      pathname: `/tournaments/manage/settings/${section}`,
      search: this.props.location.search,
      state: this.props.location.state
    });
  };

  onManageTournamentAdmins = () => {
    this.onEditTournamentClick();
  };

  onAddTournamentTeam = () => {
    this.props.history.push({
      pathname: '/tournaments/teams',
      search: `tournament=${this.getTournamentId()}`,
      state: {
        ...(this.props.location.state || {}),
        prevRoutePath: '/tournaments/manage/competition/teams',
        openAddTeam: true
      }
    });
  };

  onManageTournamentTeam = (team: any) => {
    const ownerSchoolId = this.props.user.activeSchoolId;
    this.props.history.push({
      pathname: '/tournaments/teams/members',
      search: `tournamentId=${this.getTournamentId()}&teamId=${
        team.id
      }&ownerSchoolId=${ownerSchoolId}&contextSchoolId=${team.schoolId}`,
      state: {
        prevRoutePath: '/tournaments/manage/competition/teams',
        search: this.props.location.search,
        contextSchoolName: team.school?.name || ''
      }
    });
  };

  onAssignTournamentNumbers = () => {
    this.props.history.push({
      pathname: '/tournaments/teams',
      search: `tournament=${this.getTournamentId()}`,
      state: {
        ...(this.props.location.state || {}),
        prevRoutePath: '/tournaments/manage/competition/teams',
        openAssignTournamentNumbers: true
      }
    });
  };

  onOpenGroupsGrid = () => {
    this.props.history.push({
      pathname: '/tournaments/grid',
      search: `tournament=${this.getTournamentId()}`,
      state: {
        ...(this.props.location.state || {}),
        search: this.props.location.search,
        prevPath: '/tournaments/manage/competition/groups'
      }
    });
  };

  onManageGroups = () => {
    this.props.history.push({
      pathname: '/tournaments/customGroups',
      search: `tournament=${this.getTournamentId()}`,
      state: {
        ...(this.props.location.state || {}),
        search: this.props.location.search,
        prevPath: '/tournaments/manage/competition/groups'
      }
    });
  };

  onOpenLegacyEvents = () => {
    this.props.history.push({
      pathname: '/tournaments/events',
      search: `tournament=${this.getTournamentId()}`,
      state: {
        ...(this.props.location.state || {}),
        prevPath: `/tournaments/manage/competition/${
          this.state.tournament &&
          (isTeamTournament(this.state.tournament) || isMiniTeamTournament(this.state.tournament))
            ? 'fixtures'
            : 'events'
        }`,
        search: this.props.location.search
      }
    });
  };

  onOpenTournamentEvent = (event: any, eventListState: TournamentEventListState) => {
    this.props.history.push({
      pathname: '/tournaments/events/event',
      search: `id=${event.id}`,
      state: {
        ...(this.props.location.state || {}),
        prevPath: `/tournaments/manage/competition/${
          this.state.tournament &&
          (isTeamTournament(this.state.tournament) || isMiniTeamTournament(this.state.tournament))
            ? 'fixtures'
            : 'events'
        }`,
        search: this.props.location.search,
        eventListState
      }
    });
  };

  onNotifySchools = (schools: any[]) => {
    const tournamentId = this.getTournamentId();
    const storageKey = `tournamentNotificationRecipients:${this.props.user.userId}:${tournamentId}`;
    window.sessionStorage.setItem(storageKey, JSON.stringify(schools));
    this.props.history.push({
      pathname: '/tournaments/manage/entries/eligible/notification',
      search: this.props.location.search,
      state: { ...(this.props.location.state || {}), notificationSchools: schools }
    });
  };

  onNotifyAthleteSchools = (schools: any[]) => {
    const tournamentId = this.getTournamentId();
    window.sessionStorage.setItem(`tournamentAthleteSchoolNotificationRecipients:${this.props.user.userId}:${this.props.user.activeSchoolId}:${tournamentId}`, JSON.stringify(schools));
    this.props.history.push({pathname: '/tournaments/manage/entries/athleteSchools/notification', search: this.props.location.search,
      state: {...(this.props.location.state || {}), notificationSchools: schools}});
  };

  render() {
    const { tournament, isLoading, error } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    if (error || !tournament) {
      return (
        <div className="utmManagement utmManagement--message">
          <h1>{error || 'Tournament not found.'}</h1>
          <button type="button" onClick={this.onBackClick}>
            Back to tournaments
          </button>
        </div>
      );
    }

    const start = moment(tournament.startTime);
    const end = moment(tournament.endTime);
    const type = TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING[tournament.type] || tournament.type;
    const gender = TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[tournament.gender] || tournament.gender;
    const ages = formatTournamentAges(tournament.ages || []);
    const postcode =
      (tournament.venue && tournament.venue.postcodeData && tournament.venue.postcodeData.postcode) ||
      (tournament.venue && tournament.venue.postcode) ||
      'Venue not set';
    const activeTab = this.getActiveTab();
    const sportName = tournament.sport ? tournament.sport.name : 'Sport';
    const sportColours = getSportColours(sportName);
    const bookingStatus = getBookingStatus(tournament);

    return (
      <div className="utmManagement">
        <UnionTournamentManagementPageHelp
          tournament={tournament}
          activeTab={activeTab as UnionTournamentManagementTab}
          subsection={this.props.match.params.subsection}
          confirmationSummary={this.state.confirmationSummary}
          onEdit={this.onEditTournamentClick}
          onReports={() => this.setState({ isReportsOpen: true })}
          onNotifications={this.onShowNotificationsClick}
          onTab={this.onTabClick}
          onEntriesSection={this.onEntriesSectionChange}
          onCompetitionSection={this.onCompetitionSectionChange}
          onSettingsSection={this.onSettingsSectionChange}
          onOpenEvents={this.onOpenLegacyEvents}
        />
        <header className="utmManagement__header" data-help-anchor="union-tournament-header">
          <div className="utmManagement__topbar">
            <button className="utmManagement__back" type="button" onClick={this.onBackClick}>
              <span aria-hidden="true">‹</span> All tournaments
            </button>
            <div className="utmManagement__actions" data-help-anchor="union-tournament-header-actions">
              <span className={`utmManagement__status is-${bookingStatus.tone}`} title={bookingStatus.description}>
                {bookingStatus.label}
              </span>
              <button
                className="utmManagement__reports"
                type="button"
                onClick={() => this.setState({ isReportsOpen: true })}
              >
                <svg className="utmManagement__metaIcon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5" />
                </svg>
                <span>Reports</span>
              </button>
              <button className="utmManagement__notifications" type="button" onClick={this.onShowNotificationsClick}>
                <MetaIcon path={META_ICONS.notifications} />
                <span>Notifications</span>
              </button>
              <button className="utmManagement__edit" type="button" onClick={this.onEditTournamentClick}>
                <MetaIcon path={META_ICONS.edit} />
                <span>Edit tournament</span>
              </button>
            </div>
          </div>

          <div className="utmManagement__summary">
            <div className="utmManagement__identity">
              <h1>{tournament.name}</h1>
              <div className="utmManagement__details">
                <span className="utmManagement__sport" style={sportColours}>
                  {sportName}
                </span>
                <span className="utmManagement__type">{type}</span>
                <span>
                  <MetaIcon path={META_ICONS.calendar} /> {start.format('DD MMM YYYY')} · {start.format('HH:mm')}
                </span>
                <span>
                  <MetaIcon path={META_ICONS.clock} /> {start.format('HH:mm')} – {end.format('HH:mm')}
                </span>
                <span>
                  <MetaIcon path={META_ICONS.pin} /> {postcode}
                </span>
                <span>
                  <MetaIcon path={META_ICONS.audience} /> {[gender, ages].filter(Boolean).join(' · ')}
                </span>
              </div>
            </div>
          </div>

          {!this.state.isConfirmationSummaryLoading ? (
            <div
              className="utmManagement__confirmationSummary"
              aria-label="Confirmation request summary"
              data-help-anchor="union-tournament-confirmations"
            >
              {this.state.confirmationSummary.total === 0 ? (
                <button type="button" className="is-neutral" onClick={() => this.onEntriesSectionChange('requests')}>
                  No requests sent yet
                </button>
              ) : (
                <>
                  <button type="button" className="is-sent" onClick={() => this.onEntriesSectionChange('requests')}>
                    <b>{this.state.confirmationSummary.total}</b> requests sent
                  </button>
                  {this.state.confirmationSummary.pending > 0 ? (
                    <button
                      type="button"
                      className="is-pending"
                      onClick={() => this.onEntriesSectionChange('requests')}
                    >
                      <b>{this.state.confirmationSummary.pending}</b> still pending
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="is-complete"
                      onClick={() => this.onEntriesSectionChange('requests')}
                    >
                      No replies pending
                    </button>
                  )}
                  {this.state.confirmationSummary.rejected > 0 ? (
                    <button
                      type="button"
                      className="is-rejected"
                      onClick={() => this.onEntriesSectionChange('requests')}
                    >
                      <b>{this.state.confirmationSummary.rejected}</b> rejected
                    </button>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          <nav
            className="utmManagement__tabs"
            aria-label="Tournament management sections"
            data-help-anchor="union-tournament-tabs"
          >
            {TABS.map(tab => (
              <button
                key={tab.key}
                type="button"
                className={tab.key === activeTab ? 'is-active' : ''}
                aria-current={tab.key === activeTab ? 'page' : undefined}
                onClick={() => this.onTabClick(tab.key)}
              >
                <MetaIcon path={tab.icon} />
                {tab.label}
                {tab.key === 'communications' && this.state.communicationsUnreadCount ? (
                  <b className="utmManagement__tabUnread">{this.state.communicationsUnreadCount}</b>
                ) : null}
              </button>
            ))}
          </nav>
        </header>
        <TournamentReportsLauncher
          user={this.props.user}
          tournament={tournament}
          isOpen={this.state.isReportsOpen}
          onClose={() => this.setState({ isReportsOpen: false })}
        />
        {activeTab === 'overview' ? (
          <TournamentOverview
            user={this.props.user}
            tournament={tournament}
            onOpenSection={this.onOpenTournamentSection}
            onEdit={this.onEditTournamentClick}
          />
        ) : null}
        {activeTab === 'entries' ? (
          <TournamentEntries
            user={this.props.user}
            tournament={tournament}
            activeSection={(this.props.match.params.subsection as EntriesSection) || 'rules'}
            onSectionChange={this.onEntriesSectionChange}
            onEditRules={this.onEditTournamentClick}
            onOpenLegacySection={this.onOpenTournamentSection}
            onNotifySchools={this.onNotifySchools}
            onNotifyAthleteSchools={this.onNotifyAthleteSchools}
            onUpdated={updatedTournament => this.setState({ tournament: updatedTournament })}
          />
        ) : null}
        {activeTab === 'competition' ? (
          <TournamentCompetition
            user={this.props.user}
            tournament={tournament}
            activeSection={
              (this.props.match.params.subsection as CompetitionSection) ||
              (isTeamTournament(tournament) || isMiniTeamTournament(tournament) ? 'teams' : 'competitors')
            }
            onSectionChange={this.onCompetitionSectionChange}
            onAddTeam={this.onAddTournamentTeam}
            onAssignTournamentNumbers={this.onAssignTournamentNumbers}
            onManageTeam={this.onManageTournamentTeam}
            onOpenGroupsGrid={this.onOpenGroupsGrid}
            onManageGroups={this.onManageGroups}
            onOpenCompetitionReports={() => this.setState({ isReportsOpen: true })}
            onOpenLegacyEvents={this.onOpenLegacyEvents}
            onOpenEvent={this.onOpenTournamentEvent}
            eventListState={(this.props.location.state || {}).eventListState}
          />
        ) : null}
        {activeTab === 'publishing' ? (
          <TournamentPublishing
            user={this.props.user}
            tournament={tournament}
            onUpdated={updatedTournament => this.setState({ tournament: updatedTournament })}
          />
        ) : null}
        {activeTab === 'communications' ? (
          <section className="utmCommunications">
            <nav className="utmEntries__subtabs" aria-label="Tournament communication sections">
              <button type="button" className={this.getCommunicationsSection() === 'requests' ? 'is-active' : ''} onClick={() => this.onCommunicationsSectionChange('requests')}>
                <span>Confirmation requests</span>
                <b>{this.state.confirmationSummary.total}</b>
              </button>
              <button type="button" className={this.getCommunicationsSection() === 'chats' ? 'is-active' : ''} onClick={() => this.onCommunicationsSectionChange('chats')}>
                <span>Chats</span>
              </button>
            </nav>
            {this.getCommunicationsSection() === 'requests' ? (
              <TournamentEntries
                user={this.props.user}
                tournament={tournament}
                activeSection="requests"
                onSectionChange={this.onEntriesSectionChange}
                onEditRules={this.onEditTournamentClick}
                onOpenLegacySection={this.onOpenTournamentSection}
                onNotifySchools={this.onNotifySchools}
                onUpdated={updatedTournament => this.setState({ tournament: updatedTournament })}
                hideSectionNavigation
              />
            ) : (
              <TournamentCommunications user={this.props.user} tournament={tournament} />
            )}
          </section>
        ) : null}
        {activeTab === 'settings' ? (
          <TournamentSettings
            user={this.props.user}
            tournament={tournament}
            activeSection={
              this.props.match.params.subsection === 'admins'
                ? 'admins'
                : this.props.match.params.subsection === 'communications'
                ? 'communications'
                : 'details'
            }
            onSectionChange={this.onSettingsSectionChange}
            onUpdated={updatedTournament => this.setState({ tournament: updatedTournament })}
            onManageAdmins={this.onManageTournamentAdmins}
            onDirtyChange={isSettingsDirty => this.setState({ isSettingsDirty })}
          />
        ) : null}
        <UnsavedChangesModal
          isOpen={this.state.isSettingsDiscardOpen}
          message="Your tournament settings have not been saved. If you leave this tab now, they will be lost."
          onKeepEditing={() => {
            this.pendingSettingsNavigation = undefined;
            this.setState({ isSettingsDiscardOpen: false });
          }}
          onDiscard={() => {
            const navigate = this.pendingSettingsNavigation;
            this.pendingSettingsNavigation = undefined;
            this.setState({ isSettingsDiscardOpen: false, isSettingsDirty: false }, () => navigate && navigate());
          }}
        />
      </div>
    );
  }
}
